import { isOtherCountry } from "./countryTag.js";

/**
 * The Movies and Series pages' category lists (owner, 2026-10-06): Favorites first,
 * then the new-movie categories, then everything else A to Z — the US's and those
 * with no country first, other countries' after (owner, 2026-10-09). Outside the US, the
 * viewer's own country and no country first ("Your country", 2026-10-20).
 *
 * "New" is read from the words in a provider's names, never from exact names,
 * so "new movies 2026", "New Releases" and "Latest Movies" all count. Among them
 * the newest year comes first (2026 before 2025); names without a year follow,
 * A to Z. Favorites and "All genres" are the page's own entries, not the
 * provider's, and the page places them.
 */

const NEW = /(^|[^a-z])(new|latest|recent(ly)?|releases?)([^a-z]|$)/i;

const yearOf = (name: string): number => {
  const year = name.match(/(19|20)\d{2}/);
  return year ? Number(year[0]) : 0;
};

const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base", numeric: true });

/** The provider's categories in the page's order: new ones (newest year first), then the rest A to Z; other countries' after, in each. */
export function orderMovieGenres(genres: readonly string[], viewer = "US"): { newest: string[]; rest: string[] } {
  const label = (g: string) => genreLabel(g);
  const foreign = (g: string) => (isOtherCountry(g, viewer) ? 1 : 0);
  const newest = genres
    .filter((g) => NEW.test(label(g)))
    .sort((a, b) => foreign(a) - foreign(b) || yearOf(label(b)) - yearOf(label(a)) || byName(label(a), label(b)));
  const rest = genres
    .filter((g) => !NEW.test(label(g)))
    .sort((a, b) => {
      // The provider's nameless category goes last, whatever its letters.
      if (isNameless(a) !== isNameless(b)) return isNameless(a) ? 1 : -1;
      return foreign(a) - foreign(b) || byName(label(a), label(b));
    });
  return { newest, rest };
}

/** Short words providers use as prefixes and names: kept in capitals. */
const ACRONYMS = new Set(["ppv", "ufc", "mma", "wwe", "nfl", "nba", "nhl", "mlb", "tv", "uk", "usa", "us", "hd", "4k", "lix"]);

const isNameless = (genre: string) => /^\s*(null|undefined|none)?\s*$/i.test(genre);

/**
 * How a category reads in the list: "new movies 2026" as "New Movies 2026",
 * "ar arabic movies" as "AR Arabic Movies", "ppv ufc mma" as "PPV UFC MMA". The
 * provider's nameless category ("null") reads "Uncategorized".
 */
export function genreLabel(genre: string): string {
  if (isNameless(genre)) return "Uncategorized";
  return genre
    .replace(/-/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase();
      // A two-letter language code leads many names: "ar arabic movies".
      if (ACRONYMS.has(lower) || (index === 0 && /^[a-z]{2}$/i.test(word) && genre.split(/\s+/).length > 1)) return word.toUpperCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}
